import { useEffect, useState } from "react";

/** How far outside the screen an element still counts as on it, so it is ready by the time it shows. */
const ROOT_MARGIN = "96px";
const screenWatchers = new Map<Element, (seen: boolean) => void>();
let screenObserver: IntersectionObserver | undefined;

/**
 * Whether `element` is on screen (or nearly), kept current; scrolled out of a rail it is not. One
 * IntersectionObserver serves every element. False until it has been seen and with no element;
 * true where nothing can watch.
 */
export function useOnScreen(element: Element | null): boolean {
  const [onScreen, setOnScreen] = useState(false);
  useEffect(() => {
    if (element === null) return;
    if (typeof IntersectionObserver !== "function") {
      setOnScreen(true);
      return;
    }
    screenObserver ??= new IntersectionObserver(
      (entries) => {
        for (const entry of entries) screenWatchers.get(entry.target)?.(entry.isIntersecting);
      },
      { rootMargin: ROOT_MARGIN },
    );
    screenWatchers.set(element, setOnScreen);
    screenObserver.observe(element);
    return () => {
      screenWatchers.delete(element);
      screenObserver?.unobserve(element);
    };
  }, [element]);
  return onScreen;
}
